import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useLanguage } from 'i18n/LanguageProvider';
import { appLocales } from 'i18n/messages';
import React, { useState } from 'react';
import {
  ConfiguredLanguage,
  ConfiguredLocaleCode,
  LocaleCode,
} from 'types/locale';

// Language flags for each locale
const LANGUAGE_FLAGS: Record<string, string> = {
  en: '🇺🇸',
  'en-US': '🇺🇸',
  'en-GB': '🇬🇧',
  'es-ES': '🇪🇸',
  es: '🇪🇸',
  ar: '🇦🇪',
  'bg-BG': '🇧🇬',
  'bn-IN': '🇮🇳',
  'cs-CZ': '🇨🇿',
  'de-DE': '🇩🇪',
  'el-GR': '🇬🇷',
  'fi-FI': '🇫🇮',
  'fr-CA': '🇨🇦',
  'fr-FR': '🇫🇷',
  'gu-IN': '🇮🇳',
  'he-IL': '🇮🇱',
  'hi-IN': '🇮🇳',
  'ht-HT': '🇭🇹',
  'id-ID': '🇮🇩',
  'it-IT': '🇮🇹',
  'ja-JP': '🇯🇵',
  'ka-GE': '🇬🇪',
  'kn-IN': '🇮🇳',
  'ko-KR': '🇰🇷',
  'ml-IN': '🇮🇳',
  'mr-IN': '🇮🇳',
  'ms-MY': '🇲🇾',
  'nl-NL': '🇳🇱',
  'no-NO': '🇳🇴',
  'or-IN': '🇮🇳',
  'pa-IN': '🇮🇳',
  'pl-PL': '🇵🇱',
  'pt-BR': '🇧🇷',
  'ro-RO': '🇷🇴',
  'ru-RU': '🇷🇺',
  'sv-SE': '🇸🇪',
  'ta-IN': '🇮🇳',
  'te-IN': '🇮🇳',
  'th-TH': '🇹🇭',
  'tl-PH': '🇵🇭',
  'tr-TR': '🇹🇷',
  'uk-UA': '🇺🇦',
  'vi-VN': '🇻🇳',
  'zh-CN': '🇨🇳',
  'zh-TW': '🇹🇼',
};

// Language names in their native script
const LANGUAGE_NAMES: Record<string, string> = {
  en: 'English',
  'en-US': 'English (US)',
  'en-GB': 'English (UK)',
  'es-ES': 'Español',
  es: 'Español',
  ar: 'العربية',
  'bg-BG': 'Български',
  'bn-IN': 'বাংলা',
  'cs-CZ': 'Čeština',
  'de-DE': 'Deutsch',
  'el-GR': 'Ελληνικά',
  'fi-FI': 'Suomi',
  'fr-CA': 'Français (Canada)',
  'fr-FR': 'Français',
  'gu-IN': 'ગુજરાતી',
  'he-IL': 'עברית',
  'hi-IN': 'हिन्दी',
  'ht-HT': 'Kreyòl Ayisyen',
  'id-ID': 'Bahasa Indonesia',
  'it-IT': 'Italiano',
  'ja-JP': '日本語',
  'ka-GE': 'ქართული',
  'kn-IN': 'ಕನ್ನಡ',
  'ko-KR': '한국어',
  'ml-IN': 'മലയാളം',
  'mr-IN': 'मराठी',
  'ms-MY': 'Bahasa Melayu',
  'nl-NL': 'Nederlands',
  'no-NO': 'Norsk',
  'or-IN': 'ଓଡ଼ିଆ',
  'pa-IN': 'ਪੰਜਾਬੀ',
  'pl-PL': 'Polski',
  'pt-BR': 'Português (Brasil)',
  'ro-RO': 'Română',
  'ru-RU': 'Русский',
  'sv-SE': 'Svenska',
  'ta-IN': 'தமிழ்',
  'te-IN': 'తెలుగు',
  'th-TH': 'ไทย',
  'tl-PH': 'Tagalog',
  'tr-TR': 'Türkçe',
  'uk-UA': 'Українська',
  'vi-VN': 'Tiếng Việt',
  'zh-CN': '中文 (简体)',
  'zh-TW': '中文 (繁體)',
};

// Map API language codes to app locale codes
// Maps backend codes (.locales.json / Location.language) to frontend codes (appLocales)
const LANGUAGE_CODE_MAPPING: Partial<
  Record<ConfiguredLanguage | ConfiguredLocaleCode, LocaleCode>
> = {
  en: 'en-US',
  es: 'es-ES',
  bg: 'bg-BG',
  cs: 'cs-CZ',
  de: 'de-DE',
  el: 'el-GR',
  fi: 'fi-FI',
  fr: 'fr-FR',
  he: 'he-IL',
  'hi-in': 'hi-IN',
  id: 'id-ID',
  it: 'it-IT',
  ja: 'ja-JP',
  ko: 'ko-KR',
  ms: 'ms-MY',
  nl: 'nl-NL',
  'nn-NO': 'no-NO',
  pl: 'pl-PL',
  pt: 'pt-BR',
  ro: 'ro-RO',
  ru: 'ru-RU',
  sv: 'sv-SE',
  th: 'th-TH',
  tr: 'tr-TR',
  vi: 'vi-VN',
};

interface LanguageSelectorProps {
  configuredLanguages?: ConfiguredLocaleCode[];
}

export function LanguageSelector({
  configuredLanguages,
}: LanguageSelectorProps) {
  const { currentLanguage, setLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  if (!configuredLanguages || configuredLanguages.length === 0) {
    return <></>;
  }

  const availableLanguages = configuredLanguages
    ? appLocales
        .filter(code => {
          // Check if the configured language directly matches or maps to this locale
          return configuredLanguages.some(configuredLang => {
            const mappedCode =
              LANGUAGE_CODE_MAPPING[configuredLang] ?? configuredLang;
            return mappedCode === code;
          });
        })
        .map(code => ({
          code,
          name: LANGUAGE_NAMES[code] || code,
        }))
    : appLocales.map(code => ({
        code,
        name: LANGUAGE_NAMES[code] || code,
      }));

  // Ensure we always have at least one language available
  const fallbackLanguage = { code: 'en', name: 'English' };
  const currentLanguageData =
    availableLanguages.find(lang => lang.code === currentLanguage) ??
    availableLanguages[0] ??
    fallbackLanguage;

  const handleLanguageChange = (languageCode: string) => {
    setLanguage(languageCode);
    setIsOpen(false);
  };

  return (
    <div className="flex items-center">
      <DropdownMenu.Root open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenu.Trigger className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 hover:bg-gray-50">
          <span className="flex items-center gap-2">
            <span className="text-base">
              {LANGUAGE_FLAGS[currentLanguageData.code]}
            </span>
            <span className="hidden sm:inline">{currentLanguageData.name}</span>
          </span>
          <svg
            className={`h-4 w-4 text-gray-500 transition-transform ${
              isOpen ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content className="z-50 min-w-[140px] rounded-md border border-gray-200 bg-white p-1 shadow-lg">
            {availableLanguages.map(language => (
              <DropdownMenu.Item
                key={language.code}
                onClick={() => handleLanguageChange(language.code)}
                className={`cursor-pointer rounded px-3 py-2 text-sm outline-none hover:bg-gray-100 ${
                  currentLanguage === language.code ? 'bg-gray-100' : ''
                }`}
              >
                <span className="flex items-center gap-2">
                  <span>{LANGUAGE_FLAGS[language.code]}</span>
                  <span>{language.name}</span>
                </span>
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
}
